import { memo, useCallback, useMemo } from "react";
import { Select } from "@mantine/core";
import { FILTER_COMBOBOX_PROPS, FILTER_SELECT_CLASSES } from "./filterStyles";

type Props = {
  handleChange: (
    operator: string,
    value: any,
    type: string,
    entityType: string,
  ) => void;
  fieldSelect: boolean;
  op?: string;
  value?: any;
};

// Move constants outside component
const OPS = [
  { name: "is equal to", operator: "eq", id: 1 },
  { name: "is known", operator: "known", id: 2 },
  { name: "is unknown", operator: "unknown", id: 3 },
];

const VALUES = [
  { name: "true", id: 1 },
  { name: "false", id: 2 },
];

const BooleanFilter = memo((props: Props) => {
  const {
    op,
    value,
    handleChange,
    fieldSelect,
  } = props;

  // Derive current selections from props (fully controlled)
  const selectOp = useMemo(
    () => op ? OPS.find((item) => item.operator === op) || { id: -1, name: "", operator: "" } : { id: -1, name: "", operator: "" },
    [op]
  );

  const selectValue = useMemo(
    () => value ? VALUES.find((item: any) => item.name === value) || { id: -1, name: "" } : { id: -1, name: "" },
    [value]
  );

  // Call handleChange directly on user interaction
  const handleOpChange = useCallback((val: string | null) => {
    const newOp = OPS.find(item => item.id.toString() === val);
    if (!newOp) return;

    const operator = newOp.operator || "";
    if (operator) {
      handleChange(
        operator,
        selectValue?.name || "",
        "boolean",
        "column",
      );
    }
  }, [selectValue, handleChange]);

  const handleValueChange = useCallback((val: string | null) => {
    const newValue = VALUES.find(item => item.id.toString() === val);
    if (!newValue) return;

    const operator = selectOp?.operator || "";
    if (
      operator === "known"
      || operator === "unknown"
      || (!!newValue?.name && !!operator)
    ) {
      handleChange(
        operator,
        newValue.name,
        "boolean",
        "column",
      );
    }
  }, [selectOp, handleChange]);

  // Convert options to Select format
  const opOptions = useMemo(() => OPS.map(o => ({ value: o.id.toString(), label: o.name })), []);
  const valueOptions = useMemo(() => VALUES.map(v => ({ value: v.id.toString(), label: v.name })), []);

  return (
    <>
      <Select
        disabled={fieldSelect}
        placeholder="Select Condition"
        data={opOptions}
        value={selectOp.id !== -1 ? selectOp.id.toString() : null}
        onChange={handleOpChange}
        size="xs"
        classNames={FILTER_SELECT_CLASSES}
        comboboxProps={FILTER_COMBOBOX_PROPS}
        w={150}
      />
      {selectOp?.operator && selectOp.operator !== "known" && selectOp.operator !== "unknown" && (
        <Select
          disabled={!selectOp.operator}
          placeholder="Select Value"
          data={valueOptions}
          value={selectValue.id !== -1 ? selectValue.id.toString() : null}
          onChange={handleValueChange}
          size="xs"
          classNames={FILTER_SELECT_CLASSES}
          comboboxProps={FILTER_COMBOBOX_PROPS}
          w={150}
        />
      )}
    </>
  );
});

BooleanFilter.displayName = "BooleanFilter";

export default BooleanFilter;
